import { useState, useEffect, useMemo } from 'react';
import { FileText, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useWheelScroll } from '@/hooks/useWheelScroll';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { getContactByEmail } from '@/lib/email/contact-utils';
import { renderTemplate } from '@/lib/email/template-utils';
import {
  deriveTagEntries,
  filterTemplatesByTag,
  ALL_TAG_ID,
} from '@/lib/email/template-tag-filter';
import { captureError } from '@/lib/sentry';
import type { EmailTemplate } from '@/lib/email/template-utils';

type TemplateSelectorDensity = 'composer' | 'wizard';

interface TemplateSelectorProps {
  onSelect: (template: EmailTemplate) => void;
  recipientEmail?: string;
  disabled?: boolean;
  /**
   * Tunes the visible height of each pane per surface. The composer pops over
   * a small textarea (~5 rows); the wizard renders inside a full dialog
   * (~7 rows). Both capped at 60vh so the popover never exceeds the viewport.
   * Optional — defaults to 'composer' so existing callers keep compiling.
   */
  density?: TemplateSelectorDensity;
}

// ~32px per row (px-2 py-1.5 text-sm). composer ~5 rows, wizard ~7 rows,
// both capped at 60vh. min() keeps the panes graceful when few templates
// are present (no forced empty space) while bounding long lists so they
// scroll — including when mounted in the wizard Dialog (see useWheelScroll).
const PANE_HEIGHT: Record<TemplateSelectorDensity, string> = {
  composer: 'max-h-[min(10rem,60vh)]',
  wizard: 'max-h-[min(14rem,60vh)]',
};

export function TemplateSelector({
  onSelect,
  recipientEmail,
  disabled,
  density = 'composer',
}: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState<string>(ALL_TAG_ID);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const tagPaneRef = useWheelScroll();
  const templatePaneRef = useWheelScroll();

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!currentWorkspace) return;

      try {
        const { data, error } = await supabase
          .from('email_templates')
          .select(`
            *,
            template_tag_relations (
              tag_id,
              template_tags (
                id,
                name
              )
            )
          `)
          .eq('workspace_id', currentWorkspace.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setTemplates(data || []);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    fetchTemplates();
  }, [currentWorkspace?.id]);

  const handleTemplateSelect = async (templateId: string) => {
    if (loading) return;
    setLoading(true);
    setOpen(false);

    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) throw new Error('Template not found');

      // If we have a specific recipient, resolve variables against their contact data
      if (recipientEmail) {
        const contact = await getContactByEmail(recipientEmail);
        if (contact) {
          const variables: Record<string, string> = {
            firstname: contact.firstName || '',
            lastname: contact.lastName || '',
            companyname: contact.companyName || '',
            occupation: contact.occupation || '',
            email: contact.email || '',
            phone: contact.phone || '',
          };

          const normalizedVariables = Object.fromEntries(
            Object.entries(variables).map(([key, value]) => [key.toLowerCase(), value])
          );

          const { rendered: body, error: bodyError } = renderTemplate(template.body, normalizedVariables);
          const { rendered: subject, error: subjectError } = renderTemplate(template.subject, normalizedVariables);

          if (bodyError || subjectError) {
            throw new Error('Failed to render template');
          }

          onSelect({ ...template, body, subject });
        } else {
          // Recipient email not found as a contact - pass raw template
          onSelect(template);
        }
      } else {
        // No recipient specified (e.g., bulk email campaign) - pass raw template
        // so {{variable}} placeholders are preserved for per-recipient resolution
        onSelect(template);
      }

    } catch (error) {
      console.error('Template selection error:', error);
      captureError(error as Error, { feature: 'email-template-select' });
      toast({
        title: "Error",
        description: "Failed to apply template",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewTemplate = () => {
    setOpen(false);
    window.open('/inbox?tab=templates', '_blank');
  };

  const tagEntries = useMemo(() => deriveTagEntries(templates), [templates]);
  const visibleTemplates = useMemo(
    () => filterTemplatesByTag(templates, selectedTagId),
    [templates, selectedTagId]
  );

  // Reset the filter to `All` every time the popover opens (spec: do not
  // persist the last-selected tag across opens).
  const handleOpenChange = (next: boolean) => {
    if (next) setSelectedTagId(ALL_TAG_ID);
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled || loading}
          className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
          title="Insert template"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          <span className="text-xs">Template</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[28rem] p-0" align="start">
        {templates.length === 0 ? (
          <div className="px-2 py-3 text-center text-sm text-muted-foreground">
            No templates yet
          </div>
        ) : (
          <div className="flex">
            {/* Left pane: single-select tag column */}
            <div ref={tagPaneRef} className={cn('w-40 shrink-0 overflow-y-auto', PANE_HEIGHT[density])}>
              <div className="p-1">
                {tagEntries.map((entry) => (
                  <button
                    key={entry.id}
                    onClick={() => setSelectedTagId(entry.id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                      'hover:bg-accent hover:text-accent-foreground',
                      'focus:bg-accent focus:text-accent-foreground focus:outline-none',
                      'cursor-default',
                      entry.id === selectedTagId &&
                        'bg-accent text-accent-foreground'
                    )}
                  >
                    <span className="truncate">{entry.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {entry.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <Separator orientation="vertical" className="h-auto" />

            {/* Right pane: filtered template list */}
            <div ref={templatePaneRef} className={cn('flex-1 overflow-y-auto', PANE_HEIGHT[density])}>
              <div className="p-1">
                {visibleTemplates.length > 0 ? (
                  visibleTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id)}
                      className={cn(
                        'w-full rounded-sm px-2 py-1.5 text-left text-sm',
                        'hover:bg-accent hover:text-accent-foreground',
                        'focus:bg-accent focus:text-accent-foreground focus:outline-none',
                        'cursor-default'
                      )}
                    >
                      {template.name}
                    </button>
                  ))
                ) : (
                  <div className="px-2 py-3 text-center text-sm text-muted-foreground">
                    No templates in this tag
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <Separator />
        <div className="p-1">
          <button
            onClick={handleNewTemplate}
            className={cn(
              'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:bg-accent focus:text-accent-foreground focus:outline-none',
              'cursor-default text-muted-foreground'
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            New template
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
